import React, { useEffect, useRef, useCallback } from 'react';
import { colors } from '../../styles/tokens';

interface Wave {
  y: number;
  amplitude: number;
  wavelength: number;
  speed: number;
  opacity: number;
  phase: number;
}

interface Ripple {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
}

interface Beacon {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  phase: number;
  pulseSpeed: number;
  size: number;
  driftSpeed: number;
  driftAngle: number;
}

interface Connection {
  from: number;
  to: number;
  progress: number;
  opacity: number;
  active: boolean;
}

const WAVE_CONFIG: Wave[] = [
  { y: 0.65, amplitude: 25, wavelength: 0.003, speed: 0.15, opacity: 0.04, phase: 0 },
  { y: 0.68, amplitude: 30, wavelength: 0.0025, speed: 0.12, opacity: 0.05, phase: 1 },
  { y: 0.72, amplitude: 35, wavelength: 0.002, speed: 0.1, opacity: 0.06, phase: 2 },
  { y: 0.76, amplitude: 40, wavelength: 0.0022, speed: 0.18, opacity: 0.08, phase: 0.5 },
  { y: 0.80, amplitude: 45, wavelength: 0.0018, speed: 0.14, opacity: 0.10, phase: 1.5 },
  { y: 0.85, amplitude: 50, wavelength: 0.0015, speed: 0.2, opacity: 0.12, phase: 0.8 },
  { y: 0.90, amplitude: 55, wavelength: 0.0012, speed: 0.16, opacity: 0.14, phase: 2.2 },
  { y: 0.95, amplitude: 45, wavelength: 0.001, speed: 0.22, opacity: 0.10, phase: 1.2 },
];

const CONFIG = {
  beaconCount: 5,
  maxRipples: 8,
  rippleThrottleMs: 800,
  connectionCooldown: 3,
  animationSpeed: 0.008,
};

const PALETTE = {
  gradientStops: ['#F8FAFC', '#F1F5F9', '#E2E8F0', colors.neutral[300]],
  ambient: { start: 'rgba(186, 230, 253, 0.12)', mid: 'rgba(224, 242, 254, 0.06)' },
  mouse: { start: 'rgba(147, 197, 253, 0.1)', mid: 'rgba(165, 180, 252, 0.04)' },
  beacon: '148, 163, 184',
  waveStroke: '100, 116, 139',
  waveMid: ['125, 145, 178', '135, 155, 185'],
  waveDeep: ['100, 130, 170', '115, 145, 180', '130, 155, 185'],
};

export const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const stateRef = useRef({
    time: 0,
    ripples: [] as Ripple[],
    beacons: [] as Beacon[],
    connections: [] as Connection[],
    lastConnectionTime: 0,
    lastRippleTime: 0,
    mouse: { target: { x: 0, y: 0 }, current: { x: 0, y: 0 }, isOnCanvas: false },
    dimensions: { width: 0, height: 0 },
  });

  const initBeacons = useCallback((width: number, height: number) => {
    const state = stateRef.current;
    state.beacons = [];
    state.connections = [];

    for (let i = 0; i < CONFIG.beaconCount; i++) {
      const baseX = width * (0.15 + (i / (CONFIG.beaconCount - 1)) * 0.7);
      const baseY = height * (0.25 + Math.random() * 0.35);

      state.beacons.push({
        x: baseX,
        y: baseY,
        baseX,
        baseY,
        phase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.5 + Math.random() * 0.3,
        size: 3 + Math.random() * 2,
        driftSpeed: 0.0003 + Math.random() * 0.0002,
        driftAngle: Math.random() * Math.PI * 2,
      });
    }

    for (let i = 0; i < CONFIG.beaconCount; i++) {
      for (let j = i + 1; j < CONFIG.beaconCount; j++) {
        state.connections.push({ from: i, to: j, progress: 0, opacity: 0, active: false });
      }
    }
  }, []);

  const getWaveY = useCallback((x: number, wave: Wave, time: number, height: number, ripples: Ripple[]): number => {
    const baseY = height * wave.y;
    const primary = Math.sin(x * wave.wavelength + time * wave.speed + wave.phase) * wave.amplitude;
    const secondary = Math.sin(x * wave.wavelength * 0.5 + time * wave.speed * 0.7 + wave.phase * 1.3) * (wave.amplitude * 0.4);
    const tertiary = Math.sin(x * wave.wavelength * 2 + time * wave.speed * 1.2 + wave.phase * 0.7) * (wave.amplitude * 0.15);
    const breathing = Math.sin(time * 0.08 + wave.phase) * 8;

    let rippleEffect = 0;
    for (const ripple of ripples) {
      const dx = x - ripple.x;
      const dy = baseY - ripple.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const rippleWave = Math.sin((dist - ripple.radius) * 0.05) * ripple.opacity * 15;
      const falloff = Math.max(0, 1 - dist / (ripple.maxRadius * 2));
      rippleEffect += rippleWave * falloff;
    }

    return baseY + primary + secondary + tertiary + breathing + rippleEffect;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const state = stateRef.current;

    const resize = () => {
      state.dimensions.width = window.innerWidth;
      state.dimensions.height = window.innerHeight;
      canvas.width = state.dimensions.width * dpr;
      canvas.height = state.dimensions.height * dpr;
      canvas.style.width = `${state.dimensions.width}px`;
      canvas.style.height = `${state.dimensions.height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      state.mouse.target = { x: state.dimensions.width / 2, y: state.dimensions.height / 2 };
      state.mouse.current = { x: state.dimensions.width / 2, y: state.dimensions.height / 2 };
      initBeacons(state.dimensions.width, state.dimensions.height);
    };

    resize();
    window.addEventListener('resize', resize);

    const createRipple = (x: number, y: number, maxRadius: number) => {
      state.ripples.push({ x, y, radius: 0, maxRadius, opacity: 0.4 });
      if (state.ripples.length > CONFIG.maxRipples) state.ripples.shift();
    };

    const handleMouseMove = (e: MouseEvent) => {
      state.mouse.target = { x: e.clientX, y: e.clientY };
      state.mouse.isOnCanvas = true;
      const now = Date.now();
      if (now - state.lastRippleTime > CONFIG.rippleThrottleMs) {
        createRipple(e.clientX, e.clientY, 80);
        state.lastRippleTime = now;
      }
    };

    const handleMouseLeave = () => {
      state.mouse.isOnCanvas = false;
      state.mouse.target = { x: state.dimensions.width / 2, y: state.dimensions.height / 2 };
    };

    const handleClick = (e: MouseEvent) => createRipple(e.clientX, e.clientY, 150);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('click', handleClick);

    const drawBackground = (width: number, height: number) => {
      const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
      bgGradient.addColorStop(0, PALETTE.gradientStops[0]);
      bgGradient.addColorStop(0.3, PALETTE.gradientStops[1]);
      bgGradient.addColorStop(0.6, PALETTE.gradientStops[2]);
      bgGradient.addColorStop(1, PALETTE.gradientStops[3]);
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);

      const ambientGlow = ctx.createRadialGradient(width * 0.3, height * 0.2, 0, width * 0.3, height * 0.2, width * 0.6);
      ambientGlow.addColorStop(0, PALETTE.ambient.start);
      ambientGlow.addColorStop(0.5, PALETTE.ambient.mid);
      ambientGlow.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = ambientGlow;
      ctx.fillRect(0, 0, width, height);

      if (state.mouse.isOnCanvas) {
        const { x, y } = state.mouse.current;
        const mouseGlow = ctx.createRadialGradient(x, y, 0, x, y, 200);
        mouseGlow.addColorStop(0, PALETTE.mouse.start);
        mouseGlow.addColorStop(0.5, PALETTE.mouse.mid);
        mouseGlow.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = mouseGlow;
        ctx.fillRect(0, 0, width, height);
      }
    };

    const drawConnections = () => {
      for (const conn of state.connections) {
        if (conn.opacity <= 0) continue;
        const from = state.beacons[conn.from];
        const to = state.beacons[conn.to];
        if (!from || !to) continue;

        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const cx = from.x + dx * conn.progress;
        const cy = from.y + dy * conn.progress;

        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(cx, cy);
        ctx.strokeStyle = `rgba(${PALETTE.beacon}, ${conn.opacity * 0.15})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();

        const pulseGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 4);
        pulseGlow.addColorStop(0, `rgba(${PALETTE.beacon}, ${conn.opacity * 0.4})`);
        pulseGlow.addColorStop(0.6, `rgba(${PALETTE.beacon}, ${conn.opacity * 0.15})`);
        pulseGlow.addColorStop(1, `rgba(${PALETTE.beacon}, 0)`);
        ctx.beginPath();
        ctx.arc(cx, cy, 4, 0, Math.PI * 2);
        ctx.fillStyle = pulseGlow;
        ctx.fill();
      }
    };

    const drawBeacons = (time: number) => {
      for (const beacon of state.beacons) {
        const pulse = (Math.sin(time * beacon.pulseSpeed * 0.5 + beacon.phase) + 1) / 2;

        ctx.beginPath();
        ctx.arc(beacon.x, beacon.y, beacon.size * 3 + pulse * 15, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${PALETTE.beacon}, ${(1 - pulse) * 0.06})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        const glowRadius = beacon.size * 4 + pulse * 2;
        const beaconGlow = ctx.createRadialGradient(beacon.x, beacon.y, 0, beacon.x, beacon.y, glowRadius);
        beaconGlow.addColorStop(0, `rgba(${PALETTE.beacon}, ${0.08 + pulse * 0.04})`);
        beaconGlow.addColorStop(0.6, `rgba(${PALETTE.beacon}, ${0.03 + pulse * 0.02})`);
        beaconGlow.addColorStop(1, `rgba(${PALETTE.beacon}, 0)`);
        ctx.beginPath();
        ctx.arc(beacon.x, beacon.y, glowRadius, 0, Math.PI * 2);
        ctx.fillStyle = beaconGlow;
        ctx.fill();

        const coreSize = beacon.size * 0.6 + pulse * 0.3;
        const coreGradient = ctx.createRadialGradient(beacon.x, beacon.y, 0, beacon.x, beacon.y, coreSize);
        coreGradient.addColorStop(0, `rgba(255, 255, 255, ${0.4 + pulse * 0.1})`);
        coreGradient.addColorStop(0.5, `rgba(${PALETTE.beacon}, ${0.3 + pulse * 0.1})`);
        coreGradient.addColorStop(1, `rgba(${PALETTE.beacon}, ${0.15 + pulse * 0.05})`);
        ctx.beginPath();
        ctx.arc(beacon.x, beacon.y, coreSize, 0, Math.PI * 2);
        ctx.fillStyle = coreGradient;
        ctx.fill();
      }
    };

    const drawRipples = () => {
      for (const ripple of state.ripples) {
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${PALETTE.beacon}, ${ripple.opacity * 0.5})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        if (ripple.radius > 10) {
          ctx.beginPath();
          ctx.arc(ripple.x, ripple.y, ripple.radius * 0.6, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${PALETTE.beacon}, ${ripple.opacity * 0.25})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    };

    const drawWaves = (time: number, width: number, height: number) => {
      const segments = Math.ceil(width / 3);

      WAVE_CONFIG.forEach((wave, index) => {
        ctx.beginPath();
        ctx.moveTo(-20, height + 20);
        ctx.lineTo(-20, getWaveY(0, wave, time, height, state.ripples));

        for (let i = 0; i <= segments; i++) {
          const x = (width / segments) * i;
          const y = getWaveY(x, wave, time, height, state.ripples);
          if (i === 0) {
            ctx.lineTo(x, y);
          } else {
            const prevX = (width / segments) * (i - 1);
            const prevY = getWaveY(prevX, wave, time, height, state.ripples);
            ctx.quadraticCurveTo(prevX, prevY, (prevX + x) / 2, (prevY + y) / 2);
          }
        }

        ctx.lineTo(width + 20, height + 20);
        ctx.closePath();

        const waveTop = height * wave.y - wave.amplitude;
        const fillGradient = ctx.createLinearGradient(0, waveTop, 0, height);
        const depthRatio = index / WAVE_CONFIG.length;

        if (depthRatio < 0.4) {
          fillGradient.addColorStop(0, `rgba(${PALETTE.beacon}, ${wave.opacity})`);
          fillGradient.addColorStop(0.5, `rgba(${PALETTE.beacon}, ${wave.opacity * 0.5})`);
          fillGradient.addColorStop(1, `rgba(${PALETTE.beacon}, ${wave.opacity * 0.1})`);
        } else if (depthRatio < 0.7) {
          fillGradient.addColorStop(0, `rgba(${PALETTE.waveMid[0]}, ${wave.opacity})`);
          fillGradient.addColorStop(0.4, `rgba(${PALETTE.waveMid[1]}, ${wave.opacity * 0.6})`);
          fillGradient.addColorStop(1, `rgba(${PALETTE.beacon}, ${wave.opacity * 0.15})`);
        } else {
          fillGradient.addColorStop(0, `rgba(${PALETTE.waveDeep[0]}, ${wave.opacity})`);
          fillGradient.addColorStop(0.3, `rgba(${PALETTE.waveDeep[1]}, ${wave.opacity * 0.7})`);
          fillGradient.addColorStop(0.7, `rgba(${PALETTE.waveDeep[2]}, ${wave.opacity * 0.4})`);
          fillGradient.addColorStop(1, `rgba(${PALETTE.beacon}, ${wave.opacity * 0.1})`);
        }

        ctx.fillStyle = fillGradient;
        ctx.fill();

        ctx.beginPath();
        for (let i = 0; i <= segments; i++) {
          const x = (width / segments) * i;
          const y = getWaveY(x, wave, time, height, state.ripples);
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            const prevX = (width / segments) * (i - 1);
            const prevY = getWaveY(prevX, wave, time, height, state.ripples);
            ctx.quadraticCurveTo(prevX, prevY, (prevX + x) / 2, (prevY + y) / 2);
          }
        }
        ctx.strokeStyle = `rgba(${PALETTE.waveStroke}, ${wave.opacity * 0.6})`;
        ctx.lineWidth = depthRatio > 0.6 ? 1.5 : 1;
        ctx.stroke();

        if (depthRatio > 0.7) {
          ctx.beginPath();
          for (let i = 0; i <= segments; i++) {
            const x = (width / segments) * i;
            const y = getWaveY(x, wave, time, height, state.ripples) - 1;
            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              const prevX = (width / segments) * (i - 1);
              const prevY = getWaveY(prevX, wave, time, height, state.ripples) - 1;
              ctx.quadraticCurveTo(prevX, prevY, (prevX + x) / 2, (prevY + y) / 2);
            }
          }
          ctx.strokeStyle = `rgba(255, 255, 255, ${wave.opacity * 0.5})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
    };

    const animate = () => {
      state.time += CONFIG.animationSpeed;
      const { width, height } = state.dimensions;

      state.mouse.current.x += (state.mouse.target.x - state.mouse.current.x) * 0.03;
      state.mouse.current.y += (state.mouse.target.y - state.mouse.current.y) * 0.03;

      state.ripples = state.ripples.filter(r => {
        r.radius += 1.5;
        r.opacity *= 0.985;
        return r.opacity > 0.01 && r.radius < r.maxRadius;
      });

      for (const beacon of state.beacons) {
        beacon.driftAngle += beacon.driftSpeed;
        beacon.x = beacon.baseX + Math.sin(beacon.driftAngle) * 30;
        beacon.y = beacon.baseY + Math.cos(beacon.driftAngle * 0.7) * 20;
      }

      if (state.time - state.lastConnectionTime > CONFIG.connectionCooldown && Math.random() < 0.02) {
        const inactive = state.connections.filter(c => !c.active);
        if (inactive.length > 0) {
          const conn = inactive[Math.floor(Math.random() * inactive.length)];
          conn.active = true;
          conn.progress = 0;
          conn.opacity = 0;
          state.lastConnectionTime = state.time;
        }
      }

      for (const conn of state.connections) {
        if (conn.active) {
          conn.progress += 0.008;
          conn.opacity = conn.progress < 0.5 ? conn.progress * 0.6 : (1 - conn.progress) * 0.6;
          if (conn.progress >= 1) {
            conn.active = false;
            conn.progress = 0;
            conn.opacity = 0;
          }
        }
      }

      drawBackground(width, height);
      drawConnections();
      drawBeacons(state.time);
      drawRipples();
      drawWaves(state.time, width, height);

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('click', handleClick);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [initBeacons, getWaveY]);

  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full" />;
};
